import { useState } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

import PlgIcon from "plg/shell/PlgIcon";
import type { PlgIconName } from "plg/shell/PlgIcon";
import ShortlistCountCard from "components/Screens/Marketplace/Customer/Shortlist/ShortlistCountCard";
import { useShortlist } from "components/Screens/Marketplace/Customer/Shortlist/ShortlistContext";
import usePlgSignupConfig from "plg/screens/Signup/usePlgSignupConfig";
import { getTalentDisplayName } from "components/Screens/Marketplace/Customer/utils/redaction";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import { HIRE_ROUTES } from "plg/routes";
// import { hireUnlock } from "plg/routes";
// import { UNLOCKS, UNLOCK_KEYS } from "plg/screens/Unlocks/unlocks";
import ProtoAvatar from "components/Screens/Marketplace/Customer/common/ProtoAvatar/ProtoAvatar";
import { trackPlg } from "plg/analytics/events";
import { plgEntry } from "plg/analytics/usePlgEntryTracking";
import { rememberStandardTile } from "plg/analytics/plgAnalyticsSession";
import type { PlgHomeContent } from "plg/api/plgHome";
import usePlgHomeData from "plg/screens/Home/usePlgHomeData";
import HowItWorksPopup from "plg/screens/Home/HowItWorksPopup";
import WhatHappensNextTour from "plg/screens/Home/WhatHappensNextTour";
import "./PlgHome.scss";

const USP_ICONS: Record<string, ReactElement> = {
  school: <SchoolOutlinedIcon />,
  briefcase: <WorkOutlineOutlinedIcon />,
  shield: <ShieldOutlinedIcon />,
};

// A drawn globe, so its lines can be masked away from the type (prototype GLOBE_SVG)
const GLOBE = (
  <svg viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth=".6" aria-hidden="true">
    <circle cx="100" cy="100" r="96" />
    <ellipse cx="100" cy="100" rx="40" ry="96" />
    <ellipse cx="100" cy="100" rx="72" ry="96" />
    <path d="M100 4v192" />
    <path d="M10 66h180M4 100h192M10 134h180" />
    <ellipse cx="100" cy="100" rx="96" ry="34" />
  </svg>
);

// The previous home's shortlist count and "Why practices hire through Edge" are not in the new
// design (index_updated.html); kept here, switched off.
const SHOW_PREVIOUS_HOME_CARDS = false;

function PlgHome() {
  const navigate = useNavigate();
  const api = useTalentApi();
  // The page copy comes from the database (PLG_HOME_CONFIG); the blocks show once it has loaded. Both requests are
  // kept for the tab (usePlgHomeData), so coming back to Home does not ask again.
  const { content, people } = usePlgHomeData(() =>
    api.fetchTalentList({ page: 1, limit: 3 }).then((r) => (r.talentRecords || []).slice(0, 3))
  );
  const [openTile, setOpenTile] = useState<PlgHomeContent["howItWorks"]["tiles"][number] | null>(null);
  const [tourOpen, setTourOpen] = useState(false);

  const toTalent = () => navigate(HIRE_ROUTES.talentNetwork, { state: plgEntry("home") });
  // Every tile click is logged for the account executive, then the answer opens in a popup.
  const openStandard = (tile: PlgHomeContent["howItWorks"]["tiles"][number]) => {
    trackPlg("standard_tile_clicked", { tile: tile.key });
    rememberStandardTile(tile.key);
    setOpenTile(tile);
  };
  const openTour = () => {
    trackPlg("what_happens_next_clicked", {});
    setTourOpen(true);
  };

  return (
    <section className="plg-home">
      <div className="plg-home__col">
        {/* Split card: copy on the left, a live preview of the network on the right */}
        <section className="bh-split">
          <div className="bh-copy">
            {content && (
              <>
                <h2>{content.browse.title}</h2>
                <p>{content.browse.text}</p>
              </>
            )}
            <div className="bh-actions">
              <button type="button" className="plg-btn plg-btn--primary plg-btn--lg" onClick={toTalent}>
                Browse talent <PlgIcon name="chevRight" />
              </button>
              <button type="button" className="plg-link-u bh-next" onClick={openTour}>
                What happens next?
              </button>
            </div>
          </div>
          {/* A preview of the network, not a link (product, 2026-10-23): Browse talent opens it. */}
          <div className="bh-visual">
            <div className="bh-rings">
              <span />
              <span />
              <span />
            </div>
            <div className="bh-globe">{GLOBE}</div>
            <div className="bh-orbit">
              <i />
            </div>
            {people.map((t, i) => (
              // eslint-disable-next-line no-underscore-dangle
              <div className={`bh-mini bh-mini-${i}`} key={t._id}>
                <ProtoAvatar talent={t} size={30} />
                <div>
                  <b>{getTalentDisplayName(t)}</b>
                  <span>{t.roles?.[0] || ""}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {SHOW_PREVIOUS_HOME_CARDS && <PreviousHomeCards />}

        {content && (
          <>
            {/* Prototype standardTiles: each opens its answer in a popup */}
            <h2 className="std-label">{content.howItWorks.title}</h2>
            <div className="std">
              {content.howItWorks.tiles.map((t) => (
                <button type="button" className="std-tile" key={t.key} onClick={() => openStandard(t)}>
                  <PlgIcon name={t.icon as PlgIconName} size={17} />
                  <h4>{t.label}</h4>
                  <p>{t.line}</p>
                </button>
              ))}
            </div>
          </>
        )}
        {/* "Once you hire" is hidden on Home for now; uncomment this block and the hireUnlock / UNLOCKS imports to bring it back.
        {content && (
          <>
            <h2 className="std-label">Once you hire</h2>
            <div className="unl">
              {UNLOCK_KEYS.map((key) => (
                <button type="button" className="std-tile" key={key} onClick={() => navigate(hireUnlock(key))}>
                  <PlgIcon name={UNLOCKS[key].icon} size={17} />
                  <h4>{UNLOCKS[key].label}</h4>
                  <p>{UNLOCKS[key].title}</p>
                </button>
              ))}
            </div>
          </>
        )}
        */}
        {openTile && <HowItWorksPopup tile={openTile} onClose={() => setOpenTile(null)} />}
        {tourOpen && <WhatHappensNextTour people={people} onClose={() => setTourOpen(false)} onBrowse={toTalent} />}
      </div>
    </section>
  );
}

/** The previous home's shortlist count and USPs (switched off; see SHOW_PREVIOUS_HOME_CARDS). */
function PreviousHomeCards() {
  const navigate = useNavigate();
  const { count, cap } = useShortlist();
  const { config } = usePlgSignupConfig();
  return (
    <div className="plg-home__pair">
      <div className="plg-home__count" onClick={() => navigate(HIRE_ROUTES.shortlist)} role="presentation">
        <ShortlistCountCard count={count} cap={cap} compact />
      </div>
      <div className="plg-card plg-home__usps">
        <h3>Why practices hire through Edge</h3>
        <div className="plg-home__usp-cols">
          {(config?.welcome?.usps || []).map((u) => (
            <div className="plg-home__usp" key={u.title}>
              <div className="plg-home__usp-ic">{USP_ICONS[u.icon] || <SchoolOutlinedIcon />}</div>
              <h4>{u.title}</h4>
              <p>{u.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PlgHome;
